import React, { useState } from 'react';
import { X, ToggleLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { itemsAPI } from '../../services/part2-api';
import { toast } from 'react-hot-toast';

const BulkStatusUpdateModal = ({ selectedItems, selectionMode, filters, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  
  const [formData, setFormData] = useState({
    status: 'active',
  });

  const selectedCount = selectionMode === 'ids' ? selectedItems.length : 'filtered';

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleProceed = () => {
    if ((selectionMode === 'ids' && selectedItems.length > 25) || selectionMode === 'filters') {
      setShowConfirm(true);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const payload = {
        mode: selectionMode,
        item_ids: selectionMode === 'ids' ? selectedItems : [],
        filters: selectionMode === 'filters' ? filters : {},
        operation: formData,
      };

      const response = await itemsAPI.bulkUpdateStatus(payload);
      
      const { totalMatched, totalUpdated, failedIds } = response.data;
      
      if (failedIds.length > 0) {
        toast.error(`Updated ${totalUpdated} items, ${failedIds.length} failed`);
      } else {
        toast.success(`Successfully updated ${totalUpdated} item(s) to ${formData.status}`);
      }
      
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Bulk status update error:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to update status';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (showConfirm) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
          <div className="p-6">
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <ToggleLeft className="w-6 h-6 text-amber-600" />
              </div>
            </div>
            <h3 className="text-lg font-bold text-center mb-2">Confirm Bulk Update</h3>
            <p className="text-gray-600 text-center mb-4">
              You are about to change status to <strong className={formData.status === 'active' ? 'text-green-600' : 'text-red-600'}>{formData.status.toUpperCase()}</strong> for <strong>{selectedCount}</strong> item(s).
              This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowConfirm(false)}
                disabled={loading}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1"
              >
                {loading ? 'Updating...' : 'Confirm Update'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <ToggleLeft className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Bulk Status Update</h2>
              <p className="text-sm text-gray-600">
                {selectedCount} item(s) selected
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            disabled={loading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Status */}
          <div>
            <Label>New Status</Label>
            <Select value={formData.status} onValueChange={(value) => handleInputChange('status', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Info Box */}
          <div className={`border p-4 rounded-lg ${
            formData.status === 'active' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
          }`}>
            <p className={`text-sm ${
              formData.status === 'active' ? 'text-green-800' : 'text-red-800'
            }`}>
              {formData.status === 'active' 
                ? 'Active items will be visible in your catalog and available for transactions.'
                : 'Inactive items will be hidden from default lists but can still be viewed in "Show All" mode.'}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleProceed}
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update Status'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkStatusUpdateModal;